import React, { useEffect, useMemo, useState } from 'react';

import { Flex } from '@reckon-web/flex';
import { Toggle } from '@reckon-web/toggle';
import { FieldLabel } from '@reckon-web/field';
import { Button } from '@reckon-web/button';
import { Field } from '@reckon-web/field';
import { SearchInput } from '@reckon-web/search-input';
import { Inline } from '@reckon-web/inline';
import { useTheme } from '@reckon-web/theme';
import { makeId } from '@reckon-web/utils';
import { ConfigMenu, ConfigMenuGroup } from '@reckon-web/config-menu';

import { ensureArray } from '../../../utils/array-utils';
import {
  VisibleFilters as VisibleFiltersType,
  useVisibleFilters,
} from '../../common/visible-filters';
import { useGetMembersList } from '../../../data/useGetMembersList';
import { useSearchClients } from '../../../data/useSearchClients';
import { useSearchFamilyGroups } from '../../../data/useSearchFamilyGroups';
import { MultiFilterMenu, Option } from '../../common/multi-filter-menu';
import { DateRangePicker } from '../../common/date-range-picker';
import { JobsFilter } from '../filter-helpers';
import {
  SearchUsersQueryVariables,
  useSearchUsers,
} from '../../../data/useSearchUsers';
import { filterUserList } from '../tasks/filter';
import { DisabledElementTooltip } from '../../common/disabled-element-tooltip';
import { useGetClientsByIds } from '../../../data/useGetClientsById';
import { useGetFamilyGroupsByIds } from '../../../data/useGetFamilyGroupsByIds';
import { useGetVisibleJobStatuses } from '../../../hooks/use-get-visible-job-statuses';
import { StatusBadge } from '../../common/status-badge';

const jobsVisibleFilters: {
  label: string;
  value: keyof VisibleFiltersType;
  disabled: boolean;
}[] = [
  { label: 'Assignee', value: 'jobAssignee', disabled: false },
  { label: 'Family group', value: 'allFamilyGroup', disabled: false },
  { label: 'Job start date', value: 'jobStartDate', disabled: false },
  { label: 'Job completed date', value: 'allCompletedDate', disabled: false },
];

export const Filter: React.FC<{
  filter: JobsFilter;
  setFilter: (value: Partial<JobsFilter>) => void;
  hasFiltersChanged: boolean;
  testId?: string;
  clearFilter: () => void;
  saveUserFilter: () => void;
}> = ({
  filter,
  setFilter,
  hasFiltersChanged,
  clearFilter,
  saveUserFilter,
  testId = `jobs__filter`,
}) => {
  const theme = useTheme();
  const [visibleFilters, setVisibleFilters] = useVisibleFilters();

  const visibleJobStatuses = useGetVisibleJobStatuses();

  const statusOptions = useMemo(() => {
    if (
      !visibleJobStatuses ||
      visibleJobStatuses.loading ||
      !visibleJobStatuses.data
    ) {
      return [];
    }
    return visibleJobStatuses.data.map((item) => ({
      label: item.name || '',
      value: item.cloudId,
      render: <StatusBadge label={item.name} colour={item.colour} />,
    }));
  }, [visibleJobStatuses]);

  const members = useGetMembersList();
  const partners = useSearchUsers({
    variables: { activeJobPartner: true } as SearchUsersQueryVariables,
    skip: false, // explicitly not skipping partners or managers because these operate more like the staff one in a memo
  });
  const managers = useSearchUsers({
    variables: { activeJobManager: true } as SearchUsersQueryVariables,
    skip: false, // explicitly not skipping partners or managers because these operate more like the staff one in a memo
  });
  const clients = useSearchClients({
    variables: { searchTerm: '', take: 20, includeNonClients: false },
    skip: true,
  });
  const getClientsByIds = useGetClientsByIds({ skip: true });
  const familyGroups = useSearchFamilyGroups({
    variables: { searchTerm: '', take: 20, includeNonFamilyGroups: false },
    skip: true,
  });
  const getFamilyGroupsByIds = useGetFamilyGroupsByIds({ skip: true });

  const [clientsOptions, setClientsOptions] = useState<Option<string>[]>([]);
  const [isSearchingClients, setIsSearchingClients] = useState<boolean>(false);
  const doSearchClients = async (searchTerm?: string) => {
    setIsSearchingClients(true);
    const results = (
      await clients.refetch({ searchTerm })
    ).data?.searchClients?.map(({ cloudId, name }) => ({
      label: name || '',
      value: cloudId,
    }));
    setClientsOptions(results || []);
    setIsSearchingClients(false);
  };
  const doLoadClients = async (ids: string[]) => {
    return (
      (
        await getClientsByIds.refetch({ cloudIds: ids })
      ).data?.getClientsByIds?.map(({ cloudId, name }) => ({
        label: name || '',
        value: cloudId,
      })) || []
    );
  };

  const [familyGroupsOptions, setFamilyGroupsOptions] = useState<
    Option<string>[]
  >([]);
  const [isSearchingFamilyGroups, setIsSearchingFamilyGroups] =
    useState<boolean>(false);
  const doSearchFamilyGroups = async (searchTerm?: string) => {
    setIsSearchingFamilyGroups(true);
    const results = (
      await familyGroups.refetch({ searchTerm })
    ).data?.searchFamilyGroups?.map(({ cloudId, name }) => ({
      label: name || '',
      value: cloudId,
    }));
    setFamilyGroupsOptions(results || []);
    setIsSearchingFamilyGroups(false);
  };
  const doLoadFamilyGroups = async (ids: string[]) => {
    return (
      (
        await getFamilyGroupsByIds.refetch({ cloudIds: ids })
      ).data?.getFamilyGroupsByIds?.map(({ cloudId, name }) => ({
        label: name || '',
        value: cloudId,
      })) || []
    );
  };

  // user dropdowns are a bit different to the other dropdowns. It has special sorting and items
  // so, we'll do this with a useMemo that rebuilds when users load or search term changes
  const [partnersSearchTerm, setPartnersSearchTerm] = useState<string>('');
  const partnersOptions = useMemo(() => {
    return filterUserList(partners.data, partnersSearchTerm);
  }, [partners.data, partnersSearchTerm]);

  const [managersSearchTerm, setManagersSearchTerm] = useState<string>('');
  const managersOptions = useMemo(() => {
    return filterUserList(managers?.data, managersSearchTerm);
  }, [managers.data, managersSearchTerm]);

  const [staffSearchTerm, setStaffSearchTerm] = useState<string>('');
  const staffOptions = useMemo(() => {
    return filterUserList(members.data, staffSearchTerm);
  }, [members.data, staffSearchTerm]);

  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState<string>('');
  const assigneeOptions = useMemo(() => {
    return filterUserList(members.data, assigneeSearchTerm);
  }, [members.data, assigneeSearchTerm]);

  // kick off the searches for the things, so that the lists don't start off empty. This is totally optional
  // and probably utterly pointless, but the old app always started off with populated lists
  useEffect(() => {
    doSearchClients('');
    doSearchFamilyGroups('');
    // yeah, nah, we don't want this effect to fire when those functions change. They're not stable
    // and they're also not easy to make stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (
    members.error ||
    managers.error ||
    partners.error ||
    clients.error ||
    familyGroups.error
  )
    return <div>Error!</div>;

  return (
    <Flex gap="medium" alignItems="flex-start" data={{ testid: testId }}>
      <Inline alignY="top" gap="small">
        <Field label="Filter" labelVisible={false}>
          <SearchInput
            placeholder="Filter..."
            size="small"
            onChange={(e) => setFilter({ text: e.target.value })}
            onClear={() => setFilter({ text: '' })}
            value={filter.text || ''}
            css={{ width: '128px' }}
            data-testid={makeId(testId, 'search')}
          />
        </Field>
        <Flex
          gap="xsmall"
          paddingX="xsmall"
          alignItems="center"
          height={theme.sizing.small}
        >
          <Toggle
            size="small"
            id="my-work-toggle"
            checked={Boolean(filter.myWork)}
            onChange={(myWork) => setFilter({ myWork })}
            data-testid={makeId(testId, 'my-work-toggle')}
          />
          <FieldLabel
            htmlFor="my-work-toggle"
            css={{ lineHeight: theme.typography.leading.tighter }}
            data-testid={makeId(testId, 'my-work-label')}
          >
            My work
          </FieldLabel>
        </Flex>
        <Flex
          gap="xsmall"
          paddingX="xsmall"
          alignItems="center"
          height={theme.sizing.small}
        >
          <Toggle
            size="small"
            id="overdue-toggle"
            checked={Boolean(filter.dueDateOverdue)}
            onChange={(dueDateOverdue) => setFilter({ dueDateOverdue })}
            data-testid={makeId(testId, 'overdue-toggle')}
          />
          <FieldLabel
            htmlFor="overdue-toggle"
            css={{ lineHeight: theme.typography.leading.tighter }}
            data-testid={makeId(testId, 'overdue-label')}
          >
            Overdue
          </FieldLabel>
        </Flex>
        <MultiFilterMenu
          label="Debtor"
          options={clientsOptions}
          values={ensureArray(filter.clients)}
          isLoading={isSearchingClients}
          onInputChanged={doSearchClients}
          setValues={(clients) => setFilter({ clients })}
          loadOptions={doLoadClients}
          testId={makeId(testId, 'debtor')}
        />
        {visibleFilters.jobAssignee && (
          <DisabledElementTooltip
            label="Assignee select dropdown"
            content="Cannot select individual users when My Work is on"
            childDisabled={Boolean(filter.myWork)}
          >
            <MultiFilterMenu
              label="Assignee"
              options={assigneeOptions}
              values={ensureArray(filter.assigneeIds)}
              onInputChanged={(inputValue?: string) =>
                setAssigneeSearchTerm(inputValue || '')
              }
              setValues={(assigneeIds) => setFilter({ assigneeIds })}
              disabled={Boolean(filter.myWork)}
              testId={makeId(testId, 'assignee')}
            />
          </DisabledElementTooltip>
        )}
        <DisabledElementTooltip
          label="Partner select dropdown"
          content="Cannot select individual users when My Work is on"
          childDisabled={Boolean(filter.myWork)}
        >
          <MultiFilterMenu
            label="Partner"
            options={partnersOptions}
            values={ensureArray(filter.partnerIds)}
            onInputChanged={(inputValue?: string) =>
              setPartnersSearchTerm(inputValue || '')
            }
            setValues={(partnerIds) => setFilter({ partnerIds })}
            disabled={Boolean(filter.myWork)}
            testId={makeId(testId, 'partner')}
          />
        </DisabledElementTooltip>
        <DisabledElementTooltip
          label="Manager select dropdown"
          content="Cannot select individual users when My Work is on"
          childDisabled={Boolean(filter.myWork)}
        >
          <MultiFilterMenu
            label="Manager"
            options={managersOptions}
            values={ensureArray(filter.managerIds)}
            onInputChanged={(inputValue?: string) =>
              setManagersSearchTerm(inputValue || '')
            }
            setValues={(managerIds) => setFilter({ managerIds })}
            disabled={Boolean(filter.myWork)}
            testId={makeId(testId, 'manager')}
          />
        </DisabledElementTooltip>
        <DisabledElementTooltip
          label="Staff select dropdown"
          content="Cannot select individual users when My Work is on"
          childDisabled={Boolean(filter.myWork)}
        >
          <MultiFilterMenu
            label="Staff"
            options={staffOptions}
            values={ensureArray(filter.staffIds)}
            onInputChanged={(inputValue?: string) =>
              setStaffSearchTerm(inputValue || '')
            }
            setValues={(staffIds) => setFilter({ staffIds })}
            disabled={Boolean(filter.myWork)}
            testId={makeId(testId, 'staff')}
          />
        </DisabledElementTooltip>
        <MultiFilterMenu
          label="Status"
          options={statusOptions}
          moveSelectionsToTop={false}
          values={ensureArray(filter.statusIds)}
          setValues={(statusIds) => setFilter({ statusIds })}
          testId={makeId(testId, 'status')}
        />
        <DisabledElementTooltip
          label="Due date range picker"
          content="Cannot select date when overdue is on"
          childDisabled={Boolean(filter.dueDateOverdue)}
        >
          <DateRangePicker
            label="Due"
            value={{ start: filter.dueDateBegin, end: filter.dueDateEnd }}
            onChange={({ start: dueDateBegin, end: dueDateEnd }) =>
              setFilter({ dueDateBegin, dueDateEnd })
            }
            disabled={Boolean(filter.dueDateOverdue)}
            testId={makeId(testId, 'due')}
          />
        </DisabledElementTooltip>
        {visibleFilters.allFamilyGroup && (
          <MultiFilterMenu
            label="Family group"
            options={familyGroupsOptions}
            values={ensureArray(filter.familyGroupIds)}
            isLoading={isSearchingFamilyGroups}
            onInputChanged={doSearchFamilyGroups}
            setValues={(familyGroupIds) => setFilter({ familyGroupIds })}
            loadOptions={doLoadFamilyGroups}
            testId={makeId(testId, 'family-group')}
          />
        )}
        {visibleFilters.jobStartDate && (
          <DateRangePicker
            label="Start"
            value={{
              start: filter.startDateBegin,
              end: filter.startDateEnd,
            }}
            onChange={({ start: startDateBegin, end: startDateEnd }) =>
              setFilter({ startDateBegin, startDateEnd })
            }
            testId={makeId(testId, 'start')}
          />
        )}
        {visibleFilters.allCompletedDate && (
          <DateRangePicker
            label="Completed"
            value={{
              start: filter.completedDateBegin,
              end: filter.completedDateEnd,
            }}
            onChange={({ start: completedDateBegin, end: completedDateEnd }) =>
              setFilter({ completedDateBegin, completedDateEnd })
            }
            testId={makeId(testId, 'completed')}
          />
        )}
        {hasFiltersChanged && (
          <Flex alignItems="center">
            <Button
              size="small"
              weight="none"
              tone="passive"
              label="Clear"
              onClick={clearFilter}
              data-testid={makeId(testId, 'clear')}
            />
            <Button
              size="small"
              weight="none"
              tone="passive"
              label="Save&hellip;"
              onClick={saveUserFilter}
              data-testid={makeId(testId, 'save')}
            />
          </Flex>
        )}
        <ConfigMenu trigger="More">
          <ConfigMenuGroup
            title="Job filters"
            onChange={(x) =>
              setVisibleFilters((prev: VisibleFiltersType) => ({
                ...prev,
                ...Object.fromEntries(
                  jobsVisibleFilters.map<[keyof VisibleFiltersType, boolean]>(
                    ({ value, disabled }) => [
                      value,
                      disabled || x.includes(value),
                    ]
                  )
                ),
              }))
            }
            options={jobsVisibleFilters}
            value={(
              Object.entries(visibleFilters) as [
                keyof VisibleFiltersType,
                boolean,
              ][]
            )
              .filter(([, v]) => v)
              .map(([k]) => k)}
            data-testid={makeId(testId, 'more-filters-jobs')}
          />
        </ConfigMenu>
      </Inline>
    </Flex>
  );
};
